import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './schedule.css';
import heroImg from '../assets/heroimg.png';
import scheduleMotion from '../assets/schedule-motion.gif';
import arrowIcon from '../assets/arrow.png';

gsap.registerPlugin(ScrollTrigger);

const days = [
  { id: 'day-1', label: 'Day 1', date: 'April 20, 2026', focus: 'Opening and sourcing' },
  { id: 'day-2', label: 'Day 2', date: 'April 21, 2026', focus: 'Automation and live demos' },
  { id: 'day-3', label: 'Day 3', date: 'April 22, 2026', focus: 'Markets and sustainability' },
  { id: 'day-4', label: 'Day 4', date: 'April 23, 2026', focus: 'Awards and partnerships' },
];

const schedules = {
  'day-1': [
    { time: '8:00 AM', title: 'Registration & Welcome Coffee', location: 'Main Lobby', type: 'Networking', color: 'green', detail: 'Badge pickup, buyer desk support, and orientation for first-time visitors.' },
    { time: '9:00 AM', title: 'Opening Ceremony', location: 'Main Hall A', speaker: 'Dr. Ahmed Hassan, Minister of Trade', type: 'Keynote', color: 'gold', detail: 'Official opening with regional manufacturing leaders and exhibition partners.' },
    { time: '10:30 AM', title: 'Innovation in Textile Automation', location: 'Conference Room 1', speaker: 'Sarah Mohamed, Tech Innovations Ltd.', type: 'Seminar', color: 'navy', detail: 'A practical look at automation, line visibility, and production data for garment factories.' },
    { time: '12:00 PM', title: 'Networking Lunch', location: 'Exhibition Hall', type: 'Networking', color: 'green', detail: 'Hosted networking block for buyers, exhibitors, agents, and manufacturers.' },
    { time: '2:00 PM', title: 'Sustainability in Garment Manufacturing', location: 'Conference Room 2', speaker: 'Mohamed Ali, Green Textiles Inc.', type: 'Seminar', color: 'navy', detail: 'Lower-waste processes, resource-saving equipment, and practical factory upgrades.' },
    { time: '4:00 PM', title: 'Industry 4.0 & Smart Factories', location: 'Conference Room 1', speaker: 'Fatima Ahmed, Automation Experts', type: 'Seminar', color: 'navy', detail: 'Connected machines, smart planning, and scalable systems for modern textile production.' },
    { time: '6:00 PM', title: 'Cocktail Reception', location: 'VIP Lounge', type: 'Networking', color: 'green', detail: 'A relaxed evening reception for exhibitors, VIP visitors, and hosted buyers.' },
  ],
  'day-2': [
    { time: '9:00 AM', title: 'Smart Cutting Systems Demo', location: 'Hall B Demo Zone', speaker: 'Delta Machinery Team', type: 'Product Demo', color: 'orange', detail: 'Live machinery demonstration covering precision cutting and workflow speed.' },
    { time: '10:30 AM', title: 'Digital Printing Workflow Seminar', location: 'Conference Room 1', speaker: 'Nora Samir, PrintLab MEA', type: 'Seminar', color: 'navy', detail: 'Color control, fabric preparation, finishing, and output planning.' },
    { time: '12:00 PM', title: 'Buyer Networking Break', location: 'Business Lounge', type: 'Networking', color: 'green', detail: 'Structured introductions between suppliers, factory owners, and sourcing teams.' },
    { time: '2:00 PM', title: 'Production Line Maintenance Clinic', location: 'Technical Hub', speaker: 'Cairo Textile Industries', type: 'Seminar', color: 'navy', detail: 'Reducing downtime across cutting, sewing, finishing, and inspection lines.' },
    { time: '4:30 PM', title: 'Regional Distributor Roundtable', location: 'VIP Lounge', type: 'Keynote', color: 'gold', detail: 'Market access, distributor partnerships, and regional growth discussion.' },
  ],
  'day-3': [
    { time: '9:30 AM', title: 'MEA Textile Market Outlook', location: 'Main Hall A', speaker: 'Egypt Textile Council', type: 'Keynote', color: 'gold', detail: 'Demand, sourcing shifts, investment signals, and Egypts manufacturing position.' },
    { time: '11:00 AM', title: 'Low-Waste Finishing Technologies', location: 'Hall C Showcase', speaker: 'Green Textiles Inc.', type: 'Product Demo', color: 'orange', detail: 'Equipment and process examples for more responsible finishing.' },
    { time: '12:30 PM', title: 'Networking Lunch', location: 'Exhibition Hall', type: 'Networking', color: 'green', detail: 'Open networking across exhibitor teams and visitor delegations.' },
    { time: '2:30 PM', title: 'Export Readiness Workshop', location: 'Conference Room 2', speaker: 'Trade Support Office', type: 'Seminar', color: 'navy', detail: 'Compliance, quality systems, capacity planning, and buyer expectations.' },
    { time: '5:00 PM', title: 'Hosted Buyer Meetings', location: 'Business Lounge', type: 'Networking', color: 'green', detail: 'Focused sourcing conversations for apparel, textile, and technical fabric buyers.' },
  ],
  'day-4': [
    { time: '10:00 AM', title: 'Factory Upgrade Planning Clinic', location: 'Technical Hub', speaker: 'Automation Experts', type: 'Seminar', color: 'navy', detail: 'Experts review bottlenecks, equipment selection, and upgrade priorities.' },
    { time: '12:00 PM', title: 'Product Innovation Awards', location: 'Main Hall A', type: 'Awards', color: 'gold', detail: 'Recognition for standout machinery, technology, sustainability, and production solutions.' },
    { time: '2:00 PM', title: 'Final Buyer Meetings', location: 'Business Lounge', type: 'Networking', color: 'green', detail: 'Last scheduled meeting block for active opportunities and supplier follow-up.' },
    { time: '4:00 PM', title: 'Closing Reception', location: 'VIP Lounge', type: 'Networking', color: 'green', detail: 'Final gathering for exhibitors, speakers, buyers, and partners.' },
  ],
};

const eventTypes = [
  { title: 'Keynote Speeches', text: 'Industry leaders sharing regional insight.', color: 'gold', code: '01' },
  { title: 'Technical Seminars', text: 'Expert-led sessions for production teams.', color: 'navy', code: '02' },
  { title: 'Networking Breaks', text: 'Connect with buyers, peers, and exhibitors.', color: 'green', code: '03' },
  { title: 'Product Demos', text: 'Live machinery and system demonstrations.', color: 'orange', code: '04' },
  { title: 'Award Ceremonies', text: 'Recognition for standout industry work.', color: 'gold', code: '05' },
];

const sessionFilters = ['All', 'Keynote', 'Seminar', 'Networking', 'Product Demo', 'Awards'];

const Schedule = () => {
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const [activeDay, setActiveDay] = useState('day-1');
  const [openSession, setOpenSession] = useState(1);
  const [activeFilter, setActiveFilter] = useState('All');
  const [savedSessions, setSavedSessions] = useState([]);

  const selectedDay = days.find((day) => day.id === activeDay);
  const sessions = useMemo(
    () => schedules[activeDay].filter((session) => activeFilter === 'All' || session.type === activeFilter),
    [activeDay, activeFilter]
  );
  const fullDaySessions = schedules[activeDay];
  const progress = ((days.findIndex((day) => day.id === activeDay) + 1) / days.length) * 100;

  useEffect(() => {
    setOpenSession(0);
  }, [activeDay, activeFilter]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.schedule-hero-line', {
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 1.1,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.from('.schedule-hero-item', {
        y: 50,
        opacity: 0,
        filter: 'blur(10px)',
        duration: 0.9,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.to('.schedule-orbit', {
        rotate: 360,
        duration: 26,
        repeat: -1,
        ease: 'none',
      });

      gsap.utils.toArray('[data-schedule-reveal]').forEach((item) => {
        gsap.fromTo(
          item,
          { y: 56, filter: 'blur(8px)' },
          {
            y: 0,
            filter: 'blur(0px)',
            duration: 0.85,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: item,
              start: 'top 86%',
              once: true,
            },
          }
        );
      });
    }, page);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    gsap.fromTo(
      '.schedule-row',
      { x: 34, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.07 }
    );
  }, [activeDay]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const moveHero = (event) => {
      const rect = hero.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      hero.style.setProperty('--mouse-x', `${x}px`);
      hero.style.setProperty('--mouse-y', `${y}px`);
      gsap.to('.schedule-hero-img', {
        x: (x / rect.width - 0.5) * 24,
        y: (y / rect.height - 0.5) * 18,
        duration: 0.8,
        ease: 'power3.out',
      });
    };

    window.addEventListener('mousemove', moveHero);
    return () => window.removeEventListener('mousemove', moveHero);
  }, []);

  useEffect(() => {
    const cards = pageRef.current?.querySelectorAll('.schedule-spotlight');
    if (!cards) return undefined;

    const moveSpotlight = (event) => {
      const card = event.currentTarget;
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
      card.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
    };

    cards.forEach((card) => card.addEventListener('mousemove', moveSpotlight));
    return () => cards.forEach((card) => card.removeEventListener('mousemove', moveSpotlight));
  }, [activeDay]);

  return (
    <main className="schedule-page" ref={pageRef}>
      <section className="schedule-hero" ref={heroRef}>
        <div className="schedule-hero-media">
          <img src={heroImg} alt="Textile machinery exhibition" className="schedule-hero-img" />
        </div>
        <div className="schedule-hero-overlay" />
        <span className="schedule-hero-line schedule-line-one" />
        <span className="schedule-hero-line schedule-line-two" />
        <strong className="schedule-vertical schedule-hero-item">Schedule</strong>

        <div className="schedule-hero-content">
          <span className="schedule-badge schedule-hero-item">4 Days of Innovation / April 20-23, 2026</span>
          <h1 className="schedule-hero-item">Event Schedule</h1>
          <p className="schedule-hero-item">
            Plan your visit across keynote sessions, technical seminars, live machinery
            demos, hosted buyer meetings, and evening networking.
          </p>

          <div className="schedule-actions schedule-hero-item">
            <button type="button" className="schedule-btn schedule-btn-primary">
              Download PDF
              <img src={arrowIcon} alt="" aria-hidden="true" />
            </button>
            <button type="button" className="schedule-btn schedule-btn-outline">
              Set Reminders
            </button>
          </div>
        </div>

        <aside className="schedule-hero-card schedule-spotlight schedule-hero-item">
          <div className="schedule-orbit" aria-hidden="true" />
          <span>Now Viewing</span>
          <strong>{selectedDay.label}</strong>
          <p>{selectedDay.focus}</p>
          <div className="schedule-mini-progress" aria-label={`Program progress ${Math.round(progress)} percent`}>
            <span style={{ width: `${progress}%` }} />
          </div>
        </aside>
      </section>

      <section className="schedule-day-band" data-schedule-reveal>
        <div className="schedule-day-tabs" role="tablist" aria-label="Schedule days">
          {days.map((day) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeDay === day.id}
              className={activeDay === day.id ? 'schedule-day-tab schedule-day-tab-active' : 'schedule-day-tab'}
              key={day.id}
              onClick={() => setActiveDay(day.id)}
            >
              <span>{day.label}</span>
              <strong>{day.date}</strong>
            </button>
          ))}
        </div>
      </section>

      <section className="schedule-program" data-schedule-reveal>
        <div className="schedule-program-heading">
          <div>
            <span className="schedule-badge light">Daily Program</span>
            <h2>{selectedDay.focus}</h2>
            <p>{selectedDay.date}</p>
          </div>
          <div className="schedule-agenda-count" aria-live="polite">
            <span>{savedSessions.length}</span>
            Saved sessions
          </div>
        </div>

        <div className="schedule-filter-bar" aria-label="Filter schedule sessions">
          {sessionFilters.map((filter) => (
            <button
              type="button"
              key={filter}
              className={activeFilter === filter ? 'schedule-filter is-active' : 'schedule-filter'}
              aria-pressed={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className="schedule-timeline">
          {sessions.map((session, index) => (
            <article className="schedule-row" key={`${activeDay}-${session.time}-${session.title}`}>
              <button
                type="button"
                className={openSession === index ? 'schedule-time schedule-time-active' : 'schedule-time'}
                onClick={() => setOpenSession(index)}
              >
                <span>{session.time}</span>
              </button>

              <article
                role="button"
                tabIndex="0"
                className={`schedule-session schedule-session-${session.color} schedule-spotlight${openSession === index ? ' schedule-session-open' : ''}`}
                aria-expanded={openSession === index}
                onClick={() => setOpenSession(openSession === index ? -1 : index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpenSession(openSession === index ? -1 : index);
                  }
                }}
              >
                <span className="schedule-session-bar" />
                <div className={`schedule-session-mark schedule-session-mark-${session.color}`}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="schedule-session-copy">
                  <div className="schedule-session-top">
                    <h3>{session.title}</h3>
                    <span>{session.type}</span>
                  </div>
                  <p className="schedule-meta">
                    {session.location}
                    {session.speaker ? ` / ${session.speaker}` : ''}
                  </p>
                  <strong>{openSession === index ? 'Hide Details' : 'View Details'}</strong>
                  {openSession === index && <p className="schedule-detail">{session.detail}</p>}
                  <button
                    type="button"
                    className="schedule-save"
                    onClick={(event) => {
                      event.stopPropagation();
                      const id = `${activeDay}-${session.time}-${session.title}`;
                      setSavedSessions((current) => (
                        current.includes(id)
                          ? current.filter((item) => item !== id)
                          : [...current, id]
                      ));
                    }}
                  >
                    {savedSessions.includes(`${activeDay}-${session.time}-${session.title}`) ? 'Saved' : 'Add to agenda'}
                  </button>
                </div>
              </article>
            </article>
          ))}

          {sessions.length === 0 && (
            <div className="schedule-empty schedule-spotlight">
              <h3>No sessions match this filter.</h3>
              <p>{fullDaySessions.length} sessions are still available on {selectedDay.label}.</p>
              <button type="button" onClick={() => setActiveFilter('All')}>Show all sessions</button>
            </div>
          )}
        </div>
      </section>

      <section className="schedule-types" data-schedule-reveal>
        <div className="schedule-types-heading">
          <span className="schedule-badge">Event Types</span>
          <h2>Session formats built for learning, sourcing, and real meetings.</h2>
        </div>

        <div className="schedule-type-grid">
          {eventTypes.map((type) => (
            <article className="schedule-type-card schedule-spotlight" key={type.title}>
              <div className={`schedule-type-icon schedule-type-icon-${type.color}`}>{type.code}</div>
              <h3>{type.title}</h3>
              <p>{type.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="schedule-motion-section" data-schedule-reveal>
        <div className="schedule-motion-copy">
          <span className="schedule-badge light">Live Program Energy</span>
          <h2>Follow the movement of textiles, people, and production ideas.</h2>
          <p>
            The schedule is built to help visitors move between seminars, demos,
            meetings, and networking moments without losing the rhythm of the exhibition.
          </p>
        </div>
        <div className="schedule-gif-frame">
          <img src={scheduleMotion} alt="Animated textile pattern representing motion through the exhibition schedule" />
        </div>
      </section>

      <section className="schedule-cta schedule-spotlight" data-schedule-reveal>
        <div>
          <span className="schedule-badge">Plan Your Visit</span>
          <h2>Secure your pass and build a focused exhibition agenda.</h2>
          <p>
            Use the schedule to prioritize seminars, demos, meetings, and networking
            across the four days of the exhibition.
          </p>
        </div>
        <Link to="/tickets" className="schedule-btn schedule-btn-primary">
          Book Ticket
          <img src={arrowIcon} alt="" aria-hidden="true" />
        </Link>
      </section>
    </main>
  );
};

export default Schedule;
